import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAnalysis, downloadPDF, downloadDOC } from '../utils/api';
import ContractQA from '../components/ContractQA';

const Results = () => {
  const { id } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('summary');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadAnalysis();
    // eslint-disable-next-line
  }, [id]);

  const loadAnalysis = async () => {
    try {
      const response = await getAnalysis(id);
      setAnalysis(response.data);
    } catch (err) {
      setError('Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const renderData = (data) => {
    if (!data) return 'N/A';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
      return Object.entries(data).map(([key, value]) => (
        <div key={key} className="ml-4">
          <span className="font-medium">{key}:</span> {String(value)}
        </div>
      ));
    }
    return String(data);
  };

  const getRiskScoreColor = (score) => {
    if (score >= 86) return 'text-red-600';
    if (score >= 61) return 'text-orange-600';
    if (score >= 31) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getSeverityColor = (severity) => {
    switch(severity?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await downloadPDF(id);
    } catch (err) {
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDOC = async () => {
    setDownloading(true);
    try {
      await downloadDOC(id);
    } catch (err) {
      alert('Failed to download DOC. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error || 'Analysis not found'}
        </div>
      </div>
    );
  }

  const result = analysis.analysis_result;

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8 overflow-x-hidden">
        {/* Header */}
      <div className="mb-6">
        <Link to="/dashboard" className="text-primary-600 hover:text-primary-700 mb-4 inline-flex items-center text-sm">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </Link>
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{analysis.contract_name}</h1>
            <div className="flex flex-wrap gap-2 text-xs md:text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {new Date(analysis.created_at).toLocaleDateString()}
              </span>
              <span>•</span>
              <span className="capitalize">{analysis.contract_type?.replace('_', ' ')}</span>
              <span>•</span>
              <span>{analysis.contract_country}</span>
              <span>•</span>
              <span>{analysis.page_count} pages</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white hover:bg-primary-700 px-4 py-2 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download as PDF"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {downloading ? 'Downloading...' : 'Download PDF'}
            </button>
            <button
              onClick={handleDownloadDOC}
              disabled={downloading}
              className="flex items-center justify-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download as Word Document"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download DOC
            </button>
          </div>
        </div>
      </div>

        {/* Risk Overview */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-base md:text-lg font-semibold mb-4 text-center">Overall Risk Score</h2>
            <div className={`text-center mb-4`}>
              <div className={`text-5xl md:text-6xl font-bold ${getRiskScoreColor(analysis.risk_score || 50)}`}>
                {analysis.risk_score || 50}
              </div>
              <div className="text-xs md:text-sm text-gray-500">out of 100</div>
            </div>
            <div className={`text-center py-2 px-4 rounded-lg font-semibold text-sm ${
              analysis.overall_risk === 'critical' ? 'bg-red-100 text-red-800' :
              analysis.overall_risk === 'high' ? 'bg-orange-100 text-orange-800' :
              analysis.overall_risk === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
              'bg-green-100 text-green-800'
            }`}>
              {analysis.overall_risk?.toUpperCase()} RISK
            </div>
          </div>
        </div>

        {/* Risk Breakdown */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Risk Breakdown</h2>
          {result?.risk_scores ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="text-center p-3 md:p-4 bg-gray-50 rounded-lg">
                <div className="text-xs md:text-sm text-gray-600 mb-2">Legal Risk</div>
                <div className={`text-2xl md:text-3xl font-bold ${getRiskScoreColor(result.risk_scores.legal_risk)}`}>
                  {result.risk_scores.legal_risk || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">Compliance & Enforceability</div>
              </div>
              <div className="text-center p-3 md:p-4 bg-gray-50 rounded-lg">
                <div className="text-xs md:text-sm text-gray-600 mb-2">Financial Risk</div>
                <div className={`text-2xl md:text-3xl font-bold ${getRiskScoreColor(result.risk_scores.financial_risk)}`}>
                  {result.risk_scores.financial_risk || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">Costs & Liabilities</div>
              </div>
              <div className="text-center p-3 md:p-4 bg-gray-50 rounded-lg">
                <div className="text-xs md:text-sm text-gray-600 mb-2">Fairness Score</div>
                <div className={`text-2xl md:text-3xl font-bold ${getRiskScoreColor(100 - (result.risk_scores.fairness_score || 0))}`}>
                  {result.risk_scores.fairness_score || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">Balance of Terms</div>
              </div>
              <div className="text-center p-3 md:p-4 bg-gray-50 rounded-lg">
                <div className="text-xs md:text-sm text-gray-600 mb-2">Clarity Score</div>
                <div className={`text-2xl md:text-3xl font-bold ${getRiskScoreColor(100 - (result.risk_scores.clarity_score || 0))}`}>
                  {result.risk_scores.clarity_score || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">Term Clarity</div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Risk breakdown not available</p>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div className="flex border-b overflow-x-auto">
          {[
            { id: 'summary', label: 'Summary', count: null },
            { id: 'red_flags', label: 'Red Flags', count: result?.red_flags?.length || 0 },
            { id: 'findings', label: 'Key Findings', count: result?.key_findings?.length || 0 },
            { id: 'recommendations', label: 'Recommendations', count: result?.recommendations?.length || 0 },
            { id: 'missing', label: 'Missing Protections', count: result?.missing_protections?.length || 0 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`px-3 sm:px-6 py-3 font-medium whitespace-nowrap text-sm ${
                activeSection === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4 md:p-6">
          {/* Summary Section */}
          {activeSection === 'summary' && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold mb-4">Executive Summary</h3>
              {result?.executive_summary ? (
                <div className="space-y-3">
                  {result.executive_summary.parties && (
                    <div className="p-3 bg-gray-50 rounded">
                      <span className="font-semibold text-gray-700">Parties:</span>
                      <div className="mt-1 text-gray-900">{renderData(result.executive_summary.parties)}</div>
                    </div>
                  )}
                  {result.executive_summary.primary_purpose && (
                    <div className="p-3 bg-gray-50 rounded">
                      <span className="font-semibold text-gray-700">Purpose:</span>
                      <div className="mt-1 text-gray-900">{result.executive_summary.primary_purpose}</div>
                    </div>
                  )}
                  {result.executive_summary.key_terms && (
                    <div className="p-3 bg-gray-50 rounded">
                      <span className="font-semibold text-gray-700">Key Terms:</span>
                      <div className="mt-1 text-gray-900">{result.executive_summary.key_terms}</div>
                    </div>
                  )}
                  {result.executive_summary.duration && (
                    <div className="p-3 bg-gray-50 rounded">
                      <span className="font-semibold text-gray-700">Duration:</span>
                      <div className="mt-1 text-gray-900">{result.executive_summary.duration}</div>
                    </div>
                  )}
                  {result.overall_assessment && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Overall Assessment</h4>
                      <p className="text-gray-700">{result.overall_assessment}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No executive summary available</p>
              )}
            </div>
          )}

          {/* Red Flags Section */}
          {activeSection === 'red_flags' && (
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">🚨</span>
                Red Flags
              </h3>
              {result?.red_flags && result.red_flags.length > 0 ? (
                <div className="space-y-4">
                  {result.red_flags.map((flag, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-2 ${getSeverityColor(flag.severity)}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{flag.title || `Red Flag ${idx + 1}`}</h4>
                          {flag.page && (
                            <span className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Page {flag.page}
                            </span>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(flag.severity)}`}>
                          {flag.severity?.toUpperCase()}
                        </span>
                      </div>
                      {flag.clause && (
                        <div className="mb-2 p-2 bg-white bg-opacity-50 rounded italic text-sm">
                          "{flag.clause}"
                        </div>
                      )}
                      <p className="text-sm">{flag.issue || flag.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No red flags identified</p>
              )}
            </div>
          )}

          {/* Key Findings Section */}
          {activeSection === 'findings' && (
            <div>
              <h3 className="text-xl font-semibold mb-4">Key Findings</h3>
              {result?.key_findings && result.key_findings.length > 0 ? (
                <div className="space-y-4">
                  {result.key_findings.map((finding, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${getSeverityColor(finding.severity)}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold">{finding.category || `Finding ${idx + 1}`}</h4>
                          <div className="flex items-center gap-3 mt-1">
                            {finding.page && (
                              <span className="text-xs text-gray-600 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Page {finding.page}
                              </span>
                            )}
                            {finding.location && (
                              <span className="text-xs text-gray-600">{finding.page ? '•' : ''} Location: {finding.location}</span>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(finding.severity)}`}>
                          {finding.severity?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm mb-2">{finding.description}</p>
                      {finding.impact && (
                        <div className="text-sm mb-2">
                          <span className="font-semibold">Impact:</span> {finding.impact}
                        </div>
                      )}
                      {finding.recommendation && (
                        <div className="text-sm text-green-700 bg-green-50 p-2 rounded">
                          <span className="font-semibold">💡 Recommendation:</span> {finding.recommendation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No key findings available</p>
              )}
            </div>
          )}

          {/* Recommendations Section */}
          {activeSection === 'recommendations' && (
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">💡</span>
                Recommendations
              </h3>
              {result?.recommendations && result.recommendations.length > 0 ? (
                <div className="space-y-3">
                  {result.recommendations.map((rec, idx) => (
                    <div key={idx} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          {idx + 1}
                        </span>
                        <p className="flex-1 text-sm text-gray-900">{rec}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No recommendations available</p>
              )}
            </div>
          )}

          {/* Missing Protections Section */}
          {activeSection === 'missing' && (
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <span className="text-2xl">⚠️</span>
                Missing Protections
              </h3>
              {result?.missing_protections && result.missing_protections.length > 0 ? (
                <div className="space-y-3">
                  {result.missing_protections.map((protection, idx) => (
                    <div key={idx} className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <span className="text-orange-600 flex-shrink-0">⚠️</span>
                        <p className="flex-1 text-sm text-gray-900">{protection}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No missing protections identified</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Q&A Section */}
      <ContractQA analysisId={id} />

      {/* Footer Info */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex flex-wrap gap-4 justify-between">
          <div>
            Analysis ID: <span className="font-mono">{analysis.id}</span>
          </div>
          <div>
            Processing time: {(analysis.processing_time_ms / 1000).toFixed(2)}s
          </div>
          <div>
            Tokens used: {analysis.tokens_used}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;
